import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, CalendarClock, ClipboardList, ShoppingBag, Wheat, Users,
  Store, Settings as SettingsIcon, LogOut, Menu, X, Shield, Copy, Sparkles
} from "lucide-react";
import { toast } from "sonner";

const NAV = [
  { to: "/dashboard", end: true, icon: LayoutDashboard, label: "Overview" },
  { to: "/dashboard/bake-days", icon: CalendarClock, label: "Bake Days" },
  { to: "/dashboard/orders", icon: ClipboardList, label: "Orders" },
  { to: "/dashboard/products", icon: ShoppingBag, label: "Products" },
  { to: "/dashboard/recipes", icon: Wheat, label: "Recipes" },
  { to: "/dashboard/customers", icon: Users, label: "Customers" },
  { to: "/dashboard/storefront", icon: Store, label: "Storefront" },
  { to: "/dashboard/settings", icon: SettingsIcon, label: "Settings" },
];

export function DashboardLayout() {
  const { bakery, user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const storeUrl = bakery ? `${window.location.origin}/bakery/${bakery.slug}` : "";
  const copyLink = () => {
    navigator.clipboard.writeText(storeUrl);
    toast.success("Storefront link copied!");
  };

  const NavItems = () => (
    <nav className="flex flex-col gap-1">
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-terracotta text-warmwhite" : "text-cocoa/70 hover:bg-cocoa/5"
            }`
          }
          data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
        >
          <item.icon strokeWidth={1.6} className="h-4.5 w-4.5" />
          {item.label}
        </NavLink>
      ))}
      {user?.role === "admin" && (
        <NavLink to="/admin" className="flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium text-cocoa/70 transition-colors hover:bg-cocoa/5" data-testid="nav-admin">
          <Shield strokeWidth={1.6} className="h-4.5 w-4.5" /> Admin
        </NavLink>
      )}
    </nav>
  );

  return (
    <div className="min-h-screen bg-cream">
      {/* Sidebar - desktop */}
      <aside className="fixed left-0 top-0 hidden h-screen w-64 flex-col border-r border-cocoa/10 bg-warmwhite p-5 lg:flex">
        <Logo />
        <div className="mt-8 flex-1"><NavItems /></div>
        <div className="border-t border-cocoa/10 pt-4">
          <div className="mb-3 px-1">
            <p className="truncate text-sm font-medium text-cocoa">{bakery?.name}</p>
            <p className="truncate text-xs text-cocoa/50">{user?.email}</p>
          </div>
          <Button variant="ghost" onClick={async () => { await logout(); navigate("/"); }} className="w-full justify-start rounded-xl text-cocoa/70 hover:bg-cocoa/5" data-testid="logout-btn">
            <LogOut className="mr-2 h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Top bar - mobile */}
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-cocoa/10 bg-warmwhite/90 px-4 py-3 backdrop-blur-xl lg:hidden">
        <Logo />
        <button onClick={() => setOpen(true)} aria-label="Open menu" className="text-cocoa" data-testid="mobile-menu-btn"><Menu /></button>
      </header>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-cocoa/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 bg-warmwhite p-5">
            <div className="flex items-center justify-between">
              <Logo />
              <button onClick={() => setOpen(false)} aria-label="Close menu" className="text-cocoa"><X /></button>
            </div>
            <div className="mt-8"><NavItems /></div>
            <Button variant="ghost" onClick={async () => { await logout(); navigate("/"); }} className="mt-6 w-full justify-start rounded-xl text-cocoa/70" data-testid="logout-btn-mobile">
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </Button>
          </div>
        </div>
      )}

      {/* Content */}
      <main className="lg:pl-64">
        <div className="mx-auto max-w-5xl px-5 py-8">
          {bakery && (
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h1 className="font-serif text-2xl font-semibold text-cocoa sm:text-3xl">{bakery.name}</h1>
                {!bakery.published && <span className="mt-1 inline-block rounded-full bg-cocoa/10 px-2.5 py-0.5 text-xs text-cocoa/60">Storefront not published yet</span>}
              </div>
              <Button onClick={copyLink} variant="outline" className="rounded-full border-cocoa/20 bg-warmwhite text-cocoa hover:bg-cream" data-testid="copy-storefront-link">
                <Copy className="mr-2 h-4 w-4" /> Copy storefront link
              </Button>
            </div>
          )}
          <TrialBanner bakery={bakery} />
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function TrialBanner({ bakery }) {
  if (!bakery || bakery.subscription_active) return null;
  const created = bakery.created_at ? new Date(bakery.created_at) : null;
  if (!created) return null;
  const end = new Date(created.getTime() + 14 * 24 * 60 * 60 * 1000);
  const daysLeft = Math.ceil((end - new Date()) / (24 * 60 * 60 * 1000));
  const active = daysLeft > 0;
  return (
    <div className={`mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border px-5 py-3.5 ${active ? "border-terracotta/25 bg-terracotta/8" : "border-terracotta bg-terracotta/12"}`} data-testid="trial-banner">
      <div className="flex items-center gap-2.5">
        <Sparkles strokeWidth={1.6} className="h-4.5 w-4.5 text-terracotta" />
        <p className="text-sm text-cocoa">
          {active
            ? <>You have <span className="font-semibold text-terracotta">{daysLeft} day{daysLeft !== 1 ? "s" : ""}</span> left in your free trial.</>
            : <>Your free trial has ended. <span className="font-semibold text-terracotta">Subscribe</span> to keep taking orders.</>}
        </p>
      </div>
      <NavLink to="/dashboard/settings#billing"><Button size="sm" className="rounded-full bg-terracotta text-warmwhite hover:bg-terracotta/90" data-testid="trial-upgrade-btn">{active ? "Choose a plan" : "Subscribe now"}</Button></NavLink>
    </div>
  );
}
